import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { ChevronRight } from "lucide-react";
import Header from "../components/Header";
import HeroSection from "../components/HeroSection";
// import ServiceHighlights from "../components/ServiceHighlights";
import ServicesOverview from "../components/ServicesOverview";
import FAQ from "../components/FAQ";
import Footer from "../components/Footer";
import PartnerMarquee from "../components/PartnerMarquee";
import HelpButton from "../components/HelpButton";
import { generateLocalBusinessSchema } from "../lib/schema";

// Cities organized by first letter
const citiesByLetter: { [key: string]: string[] } = {
  A: [
    "Aalsmeer",
    "Alblasserdam",
    "Alkmaar",
    "Almelo",
    "Almere",
    "Alphen aan den Rijn",
    "Amersfoort",
    "Amstelveen",
    "Amsterdam",
    "Apeldoorn",
    "Arnhem",
    "Assen",
    "Asten",
    "Axel",
  ],
  B: ["Barendrecht", "Beek", "Bergen op Zoom", "Beverwijk", "Boxtel", "Breda"],
  C: ["Capelle aan den IJssel", "Coevorden", "Cuijk"],
  D: [
    "Delft",
    "Den Bosch",
    "Den Haag",
    "Deventer",
    "Diemen",
    "Doetinchem",
    "Dongen",
    "Dordrecht",
    "Dronten",
    "Duiven",
  ],
  E: [
    "Echt",
    "Ede",
    "Eemnes",
    "Eindhoven",
    "Elburg",
    "Emmeloord",
    "Emmen",
    "Enkhuizen",
    "Enschede",
  ],
  F: ["Franeker"],
  G: ["Geldrop", "Geleen", "Goes", "Gorinchem", "Gouda", "Groningen"],
  H: [
    "Haarlem",
    "Hardenberg",
    "Harderwijk",
    "Heerlen",
    "Heerenveen",
    "Heerhugowaard",
    "Helmond",
    "Hengelo",
    "Hilversum",
    "Hoensbroek",
    "Hoofddorp",
    "Hoogeveen",
    "Hoorn",
    "Houten",
    "Huizen",
  ],
  I: ["IJmuiden"],
  J: ["Joure"],
  K: ["Kampen", "Katwijk"],
  L: ["Leeuwarden", "Leerdam", "Leiden", "Leiderdorp", "Lelystad", "Lochem"],
  M: ["Maassluis", "Maastricht", "Meppel", "Middelburg"],
  N: ["Naaldwijk", "Nijkerk", "Nijmegen"],
  O: ["Oegstgeest", "Oosterhout", "Oss"],
  P: ["Papendrecht", "Purmerend"],
  Q: [],
  R: ["Raalte", "Ridderkerk", "Roermond", "Roosendaal", "Rotterdam"],
  S: [
    "Schagen",
    "Schiedam",
    "Sittard",
    "Sliedrecht",
    "Sneek",
    "Soest",
    "Spijkenisse",
  ],
  T: ["Terneuzen", "Tilburg"],
  U: ["Uden", "Uithoorn", "Urk", "Utrecht"],
  V: [
    "Veenendaal",
    "Veldhoven",
    "Venlo",
    "Venray",
    "Vlaardingen",
    "Vlissingen",
    "Vught",
  ],
  W: [
    "Wageningen",
    "Waalwijk",
    "Weert",
    "Winterswijk",
    "Woerden",
    "Wormerveer",
  ],
  X: [],
  Y: ["Yerseke"],
  Z: [
    "Zaandam",
    "Zaltbommel",
    "Zandvoort",
    "Zeist",
    "Zoetermeer",
    "Zutphen",
    "Zwolle",
  ],
};

// Get all letters that have cities
const letters = Object.keys(citiesByLetter).sort();

// Flatten cities for total count
const allCities = Object.values(citiesByLetter).flat();

function Regios() {
  return (
    <div className="min-h-screen bg-gray-50 page-transition pt-[99px] md:pt-[133px]">
      <SEO
        title="Elektricien in uw regio | Elektraaanleggen.nl"
        description="Lokale expertise in elektrotechnische installaties. Werkzaam in heel Nederland met snelle service."
        schema={generateLocalBusinessSchema()}
        image="https://elektraaanleggen.nl/images/banner.png"
      />

      <Header />
      <HeroSection
        title={`Elektricien in uw regio`}
        description="Lokale expertise in elektrotechnische installaties. Werkzaam in heel Nederland met snelle service."
        imageSrc="/images/mark.png"
      />

      <section className="mx-auto w-[calc(100%-2.5rem)] max-w-[1260px] mb-12 md:mb-16">
        <h2 className="font-bold text-[24px] lg:text-[36px] text-[#17354A] mb-8">
          Waar zijn wij werkzaam?
          <span className="text-base font-normal text-gray-500 ml-2">
            ({allCities.length} steden)
          </span>
        </h2>
        <div className="space-y-8">
          {letters.map((letter) => (
            <div key={letter}>
              <h3 className="text-2xl font-bold text-[#138299] mb-4">
                {letter}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {citiesByLetter[letter].map((city) => (
                  <Link
                    key={city}
                    to={`/regios/${city.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center p-6 bg-white rounded-2xl border border-[#E5E7EB] hover:border-[#17354A]/50 transition-colors duration-150"
                  >
                    <span className="text-[#17354A] text-lg font-medium">
                      {city}
                    </span>
                    <ChevronRight className="w-5 h-5 text-[#17354A] ml-auto" />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <ServicesOverview />
      <FAQ />

      <PartnerMarquee />

      <div className="w-full cursor-pointer bg-[#2FB6D2] transition-opacity duration-150 hover:opacity-80">
        <Link
          to="/offerte"
          aria-label="Vraag een vrijblijvende offerte aan voor groepenkast vervanging"
        >
          <section className="mx-auto w-[calc(100%-2.5rem)] max-w-[1260px] text-center py-6 justify-between items-center">
            <div
              data-variant="h2"
              className="font-bold leading-[1.25] text-[28px] lg:text-[36px] self-center text-white mb-0"
            >
              Gratis offerte
            </div>
          </section>
        </Link>
      </div>

      <HelpButton />
      <Footer />
    </div>
  );
}

export default Regios;
